import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      this.#quantityChangeQueued = false;
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;
  #mutationPending = false;
  #quantityChangeQueued = false;

  #queueQuantityChange = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent) || !(event.target instanceof Node) || !this.contains(event.target)) return;
    if (this.#mutationPending || this.#needsRefresh) return;
    this.#quantityChangeQueued = true;
    this.#debouncedOnChange(event);
  };

  #beforeCheckout = (event) => {
    if (event.submitter?.name !== 'checkout') return;
    if (!this.contains(event.target) && !this.contains(event.submitter)) return;
    if (this.#quantityChangeQueued || this.#mutationPending || this.#needsRefresh) event.preventDefault();
  };
  #needsRefresh = false;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh - running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(async ({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          await this.#morphCartSection(sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself - no fallback needed.
          await sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') this.#showRefreshError(error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh - the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(async ({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          await this.#morphCartSection(sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          await sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') this.#showRefreshError(error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#queueQuantityChange);
    document.addEventListener('submit', this.#beforeCheckout, true);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#queueQuantityChange);
    document.removeEventListener('submit', this.#beforeCheckout, true);
    this.#debouncedOnChange.cancel?.();
    this.#quantityChangeQueued = false;
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    if (this.#mutationPending || this.#needsRefresh) return;
    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    // A row disappears only after Shopify confirms the authoritative cart.
    return this.updateQuantity({ line, quantity: 0, action: 'clear' });
  }

  /** Update once; reconcile uncertain outcomes with reads, never another mutation. */
  async updateQuantity(config) {
    if (this.#mutationPending || this.#needsRefresh) return;
    const { line, quantity } = config;
    const row = this.refs.cartItemRows[line - 1];
    if (!row?.dataset.key) return;
    this.#mutationPending = true;
    const marker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const hadFocus = row.contains(document.activeElement);
    this.#disableCartItems();
    row.setAttribute('aria-busy', 'true');
    this.refs.cartTotal?.shimmer();

    const sections = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item.dataset.sectionId) sections.add(item.dataset.sectionId);
    });
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: quantity > 0 ? 'update' : 'remove', context: 'cart',
      lines: [{ id: row.dataset.key, quantity }], promise: deferred.promise,
    }));

    let cart;
    let reconciled = false;
    let rejected = false;
    let rejectionMessage = '';
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', {
          signal: controller.signal,
          body: JSON.stringify({ id: row.dataset.key, quantity, sections: [...sections].join(','), sections_url: window.location.pathname }),
        }));
        // A 4xx is a definite rejection. Network/5xx/invalid success responses
        // can follow acceptance, so verify those with a read instead of retrying.
        rejected = response.status >= 400 && response.status < 500;
        const data = await response.json();
        if (rejected) rejectionMessage = typeof data.description === 'string' ? data.description : typeof data.errors === 'string' ? data.errors : '';
        if (!response.ok || data.errors || !this.#isCart(data)) throw new Error('Cart update was not confirmed');
        cart = data;
      } catch (error) {
        clearTimeout(timeout);
        if (rejected) {
          this.#handleCartError(line, { errors: rejectionMessage || this.dataset.cartUpdateError });
          deferred.reject(error);
          return;
        }
        cart = await this.fetchCartData();
        reconciled = true;
        if (!this.#isCart(cart)) throw new Error('Cart verification returned invalid data');
      } finally {
        clearTimeout(timeout);
      }

      // Publish the actual count even if its presentation later needs a refresh.
      this.#updateQuantitySelectors(cart);
      let html = cart.sections?.[this.sectionId];
      let renderError;
      try {
        if (!html) html = await sectionRenderer.getSectionHTML(this.sectionId, false);
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        if (!parsed.getElementById(`shopify-section-${this.sectionId}`)) throw new Error('Cart section is missing');
        await morphSection(this.sectionId, html, { mode: this.isDrawer ? 'hydration' : 'full' });
        this.querySelector('[data-cart-refresh-error]')?.remove();
        this.#updateCartQuantitySelectorButtonStates();
        if (reconciled && (cart.items.find((item) => item.key === row.dataset.key)?.quantity ?? 0) !== quantity) {
          this.#showRefreshError(new Error('Requested quantity was not confirmed'));
          this.querySelector('[data-cart-refresh-error]').textContent = Theme.translations.cart_change_unconfirmed;
        }
        if (hadFocus && (!document.activeElement?.isConnected || document.activeElement === document.body)) {
          const target = this.refs.cartItemRows?.[Math.min(line - 1, this.refs.cartItemRows.length - 1)]?.querySelector('button, input, a')
            || this.querySelector('a, button');
          target?.focus({ preventScroll: true });
        }
      } catch (error) {
        renderError = error;
        this.#needsRefresh = true;
        this.#showRecoveryNotice(Theme.translations.cart_display_error);
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { sections: renderError ? {} : { ...cart.sections, [this.sectionId]: html }, items: cart.items,
          itemCount: cart.item_count, source: 'cart-items-component', didError: false },
      });
    } catch (error) {
      this.#needsRefresh = true;
      this.#showRecoveryNotice(Theme.translations.cart_outcome_unknown);
      deferred.reject(error);
    } finally {
      resetShimmer(this);
      row.removeAttribute('aria-busy');
      this.#mutationPending = false;
      this.#enableCartItems();
      if (this.#needsRefresh) {
        // Do not reuse stale line state after an unknown outcome. Navigation
        // stays available; a fresh page restores the authoritative controls.
        this.querySelectorAll('button, input').forEach((control) => { control.disabled = true; });
      }
      cartPerformance.measureFromMarker(marker);
    }
  }

  #isCart(cart) {
    return Array.isArray(cart?.items) && Number.isInteger(cart.item_count) && cart.item_count >= 0;
  }

  #showRecoveryNotice(message) {
    this.#showRefreshError(new Error(message));
    const notice = this.querySelector('[data-cart-refresh-error]');
    notice.textContent = `${message} `;
    const link = document.createElement('a');
    link.href = Theme.routes.cart_url;
    link.textContent = Theme.translations.cart_refresh;
    notice.append(link);
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    const errorMessage = parsedResponseText.errors || this.dataset.cartUpdateError || '';

    if (quantityInput) quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (cartItemError instanceof HTMLElement) cartItemError.textContent = errorMessage;
    if (cartItemErrorContainer instanceof HTMLElement) cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage,
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            await startViewTransition(() => this.#morphCartSection(cartItemsHtml, morphOptions), ['fill-cart-drawer']);
          } else {
            await this.#morphCartSection(cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          await sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') this.#showRefreshError(error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
    this.setAttribute('aria-busy', 'true');

    const status = this.parentElement?.querySelector('[data-cart-updating-status]');
    if (status instanceof HTMLElement) status.textContent = status.dataset.cartUpdatingStatus || '';
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.removeAttribute('aria-busy');

    const status = this.parentElement?.querySelector('[data-cart-updating-status]');
    if (status instanceof HTMLElement) status.textContent = '';
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  #showRefreshError(error) {
    console.warn('[cart-items] Cart refresh failed:', error);
    let notice = this.querySelector('[data-cart-refresh-error]');
    if (!notice) {
      notice = document.createElement('p');
      notice.dataset.cartRefreshError = '';
      notice.className = 'cart-items__error-text';
      notice.setAttribute('role', 'alert');
      this.prepend(notice);
    }
    notice.textContent = this.dataset.cartUpdateError || Theme.translations.cart_update_error;
  }

  // A malformed bundled section must leave the existing cart usable and explain the failed refresh.
  async #morphCartSection(html, options) {
    try {
      await morphSection(this.sectionId, html, options);
      this.querySelector('[data-cart-refresh-error]')?.remove();
    } catch (error) {
      this.#showRefreshError(error);
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(`${Theme.routes.cart_url}.json`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store',
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
        return await response.json();
      } finally {
        clearTimeout(timeout);
      }
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
